/**
 * Admin `PendingContentApi` v1 client.
 *
 * Wraps the four admin operations `ListPendingContentV1`,
 * `SubmitPendingContentV1`, `ApprovePendingContentV1`, and
 * `RejectPendingContentV1` per `models/smithy/pending-content-api.smithy`.
 *
 * Every request is authenticated with `Authorization: Bearer
 * <id-token>` — the caller threads in the Cognito ID token from
 * the auth slice. The admin gateway's server-side admin-group check
 * (`services/src/api-layer/managers/require-admin-group.ts`) rejects
 * non-admin tokens with 401.
 *
 * Round-22 reconciliation: the admin gateway no longer requires an
 * `x-api-key`. The Cognito authorizer + admin-group check IS the
 * auth boundary.
 *
 * SWR cache bypass: admin data mutates via approve/reject and must
 * always reflect the latest server state. This client uses
 * `globalThis.fetch` directly.
 */

import { getAdminApiConfig } from './config';

/** Content type discriminator. */
export type PendingContentType = 'fact' | 'news' | 'release';

/**
 * Every possible queue-row status. The historical singleton
 * `'pending'` was widened 2026-09-12 so the date-scoped admin day-
 * preview surface (`AdminPendingForDate`) can render items of every
 * status (approved / rejected / system-auto-approved / denied)
 * alongside pending ones — the admin needs full visibility over a
 * date's queue traffic to control future publish content.
 */
export type PendingItemStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'system-auto-approved'
  | 'denied';

/** One queue row. Mirrors the Smithy `PendingContentItem`. */
export interface PendingContentItem {
  readonly id: string;
  readonly type: PendingContentType;
  readonly source: string;
  readonly body: string;
  readonly ingestedAt: string;
  readonly autoApproveAt: string;
  readonly status: PendingItemStatus;
  readonly version: number;
  /**
   * Optional Pacific-YMD (`YYYY-MM-DD`) target publish date. Landed
   * 2026-09-12 alongside the audit `targetDate` fix so admin
   * surfaces can filter queue rows by their intended publish day
   * without relying on `autoApproveAt` (which is absent on
   * operator-loaded fact rows).
   */
  readonly intendedPublishDate?: string;
}

/** Response for `ListPendingContentV1`. */
export interface ListPendingResponse {
  readonly items: ReadonlyArray<PendingContentItem>;
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly hasMore: boolean;
}

/** Response for `SubmitPendingContentV1`. */
export interface SubmitPendingResponse {
  readonly id: string;
  readonly status: 'pending';
  readonly ingestedAt: string;
  readonly autoApproveAt: string;
  readonly version: number;
}

/** Response for `ApprovePendingContentV1`. */
export interface ApproveResponse {
  readonly id: string;
  readonly status: 'approved';
  readonly approvedAt: string;
  readonly version: number;
}

/** Input for `UpdatePendingContentV1` (edit-in-queue, 2026-09-13). */
export interface UpdatePendingInput {
  readonly id: string;
  readonly contentType: PendingContentType;
  /** New JSON envelope body (serialized as a string). */
  readonly body: string;
  readonly expectedVersion: number;
}

/** Response for `UpdatePendingContentV1`. */
export interface UpdatePendingResponse {
  readonly id: string;
  readonly status: 'pending';
  readonly lastActionAt: string;
  readonly version: number;
}

/** Response for `RejectPendingContentV1`. */
export interface RejectResponse {
  readonly id: string;
  readonly status: 'rejected';
  readonly rejectedAt: string;
  readonly reason: string;
  readonly version: number;
}

/**
 * Discriminator over the four admin-editable target tables — mirrors
 * `PostContentTableName` from `models/smithy/pending-content-api.smithy`.
 */
export type PostContentTableName = 'facts' | 'news' | 'releases' | 'games';

/** Input shape for `PostContentV1` (`content-ingestion-pipeline` Sub-R5.1). */
export interface PostContentInput {
  readonly tableName: PostContentTableName;
  /** Record identity — SK for envelope tables, PK (`game_id`) for games. */
  readonly uuid: string;
  /**
   * Target record's PK date — required for envelope tables:
   * `MM-DD` for facts, `YYYY-MM-DD` for news + releases. Omit for games.
   */
  readonly date?: string;
  /**
   * JSON envelope patch — allow-listed keys per target table (see
   * `services/src/api-layer/managers/request-validator.ts`).
   */
  readonly patch: Readonly<Record<string, unknown>>;
  /** Caller's view of `metadata.version` at edit time (optimistic-concurrency token). */
  readonly expectedVersion: number;
}

/** Response for `PostContentV1`. */
export interface PostContentResponse {
  readonly id: string;
  readonly tableName: PostContentTableName;
  readonly updatedAt: string;
  readonly version: number;
}

function headers(idToken: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
  };
}

async function post<T>(
  path: string,
  body: unknown,
  idToken: string,
): Promise<T> {
  const { adminApiInvokeUrl } = getAdminApiConfig();
  if (adminApiInvokeUrl.length === 0) {
    throw new Error('admin-api-invoke-url-missing');
  }
  const trimmed = adminApiInvokeUrl.replace(/\/+$/, '');
  const response = await fetch(`${trimmed}${path}`, {
    method: 'POST',
    headers: headers(idToken),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let bodyText: string;
    try {
      bodyText = await response.text();
    } catch {
      bodyText = '(no body)';
    }
    throw new Error(`admin-api-${response.status}: ${bodyText}`);
  }
  return (await response.json()) as T;
}

/**
 * Fetch a page of queue items. `page` defaults to 1.
 *
 * When `intendedPublishDate` is provided (Pacific `YYYY-MM-DD`),
 * the response includes items of EVERY status
 * (`pending` / `approved` / `rejected` / `system-auto-approved` /
 * `denied`) for that day so an admin day-preview surface can render
 * the full queue traffic for a specific calendar day.
 *
 * When `intendedPublishDate` is absent, the response is restricted
 * to `status='pending'` for the `/admin/pending` review page.
 */
export function listPending(
  args: { readonly page?: number; readonly intendedPublishDate?: string },
  idToken: string,
): Promise<ListPendingResponse> {
  return post<ListPendingResponse>('/ListPendingContentV1', args, idToken);
}

export interface SubmitPendingInput {
  readonly type: PendingContentType;
  readonly source: string;
  readonly body: string;
}

/** Submit a new pending item. */
export function submitPending(
  input: SubmitPendingInput,
  idToken: string,
): Promise<SubmitPendingResponse> {
  return post<SubmitPendingResponse>('/SubmitPendingContentV1', input, idToken);
}

/** Approve a pending item by id. */
export function approvePending(
  args: { readonly id: string },
  idToken: string,
): Promise<ApproveResponse> {
  return post<ApproveResponse>('/ApprovePendingContentV1', args, idToken);
}

/** Reject a pending item with a required reason (1-500 chars). */
export function rejectPending(
  args: { readonly id: string; readonly reason: string },
  idToken: string,
): Promise<RejectResponse> {
  return post<RejectResponse>('/RejectPendingContentV1', args, idToken);
}

/**
 * Edit-in-queue for a pending item (2026-09-13). Replaces the queue
 * row's JSON envelope `body` in place with optimistic-concurrency
 * gating on `expectedVersion` (R4.7). Status stays `pending`; the
 * admin subsequently approves via approvePending to promote onto
 * the target table.
 */
export function updatePending(
  input: UpdatePendingInput,
  idToken: string,
): Promise<UpdatePendingResponse> {
  return post<UpdatePendingResponse>('/UpdatePendingContentV1', input, idToken);
}

/**
 * Save-to-server for an on-page admin edit
 * (`content-ingestion-pipeline` Sub-R5.1). Serializes the `patch`
 * object into the JSON-string wire shape the Smithy IDL expects.
 * Callers surface the response to the reducer + trigger a day-cache
 * invalidation (the DailyPage refetches via `fetchContent`).
 */
export function postContent(
  input: PostContentInput,
  idToken: string,
): Promise<PostContentResponse> {
  const body: Record<string, unknown> = {
    tableName: input.tableName,
    uuid: input.uuid,
    patch: JSON.stringify(input.patch),
    expectedVersion: input.expectedVersion,
  };
  if (input.date !== undefined) body.date = input.date;
  return post<PostContentResponse>('/PostContentV1', body, idToken);
}
